import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';
import { authService } from '../../services/authService';
import { extractApiError } from '../../services/apiClient';
// @ts-ignore
import SurveyConnectLogo from '../../assets/survey_connect_logo.png';

export const LoginScreen: React.FC = () => {
  const [phone, setPhone] = useState('+263');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'CLIENT' | 'SURVEYOR'>('CLIENT');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState<'email' | 'confirm'>('email');
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const { login, isLoading, error, clearError, setError } = useAuthStore();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    clearError();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await login({ phone, password });
    } catch { }
  };

  // If login fails because the account is unverified, navigate to OTP verify screen
  const handleSubmitWithVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await login({ phone, password });
    } catch (err: any) {
      const msg = extractApiError(err);
      if (msg && msg.toLowerCase().includes('verify') && msg.toLowerCase().includes('otp')) {
        try {
          const resp = await authService.requestOtp(phone);
          if (resp?.requires_otp === false) {
            setError(resp.detail || 'Your account is already verified. Please sign in.');
            return;
          }
          const identifier = resp?.identifier || phone;
          const identifier_type = resp?.identifier_type || (phone ? 'phone' : undefined);
          const sent_to = resp?.sent_to;
          navigate('/verify-otp', { state: { identifier, identifier_type, sent_to } });
        } catch (otpErr) {
          setError(extractApiError(otpErr));
        }
      }
    }
  };

  const openReset = () => {
    setResetOpen(true);
    setResetStep('email');
    setResetOtp('');
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetMessage('');
    setResetError('');
  };

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');
    setResetLoading(true);
    try {
      await authService.requestPasswordReset(resetEmail.trim());
      setResetStep('confirm');
      setResetMessage('A reset code has been sent to that email.');
    } catch (err) {
      setResetError(extractApiError(err));
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');
    if (resetPassword !== resetPasswordConfirm) {
      setResetError('Passwords do not match.');
      return;
    }
    setResetLoading(true);
    try {
      await authService.confirmPasswordReset({
        email: resetEmail.trim(),
        otp: resetOtp.trim(),
        new_password: resetPassword,
      });
      setResetMessage('Password reset successfully. You can sign in now.');
      setResetStep('email');
      setResetOtp('');
      setResetPassword('');
      setResetPasswordConfirm('');
    } catch (err) {
      setResetError(extractApiError(err));
    } finally {
      setResetLoading(false);
    }
  };

  const ACCOUNT_STATUSES: { role: string; value: 'CLIENT' | 'SURVEYOR'; icon: string; desc: string; bg: string }[] = [
    { role: 'Client', value: 'CLIENT', icon: '🏠', desc: 'Request surveys', bg: 'var(--sc-success-bg)' },
    { role: 'Surveyor', value: 'SURVEYOR', icon: '👷', desc: 'Browse jobs', bg: 'var(--sc-info-bg)' },
  ];

  return (
    <div style={styles.pageContainer}>
      <div style={styles.card} className="sc-animate-scale-in">
        {/* Brand Logo */}
        <div style={styles.logoContainer}>
          <img
            src={SurveyConnectLogo}
            alt="Survey Connect Logo"
            style={styles.logo}
          />
        </div>

        <h2 style={styles.title}>Sign in</h2>
        <p style={styles.subtitle}>Enter your phone number to access your account</p>

        {/* Account Status */}
        <div style={styles.demoBox}>
          <p style={styles.demoLabel}>ACCOUNT STATUS</p>
          <div style={styles.demoGrid}>
            {ACCOUNT_STATUSES.map((d) => (
              <button
                key={d.role}
                type="button"
                onClick={() => setSelectedStatus(d.value)}
                disabled={isLoading}
                style={{
                  ...styles.demoCard,
                  borderColor: selectedStatus === d.value ? 'var(--sc-brand-primary)' : 'var(--sc-border-light)',
                  background: selectedStatus === d.value ? 'var(--sc-bg-active)' : 'var(--sc-bg-input)',
                }}
                className="sc-hover-scale"
              >
                <div style={{ ...styles.demoIcon, background: d.bg }}>{d.icon}</div>
                <span style={styles.demoRoleName}>{d.role}</span>
                <span style={styles.demoRoleDesc}>{d.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>sign in with password</span>
          <div style={styles.dividerLine} />
        </div>

        {/* Error Notification */}
        {error && (
          <div style={styles.errorBox} className="sc-animate-toast-in">
            <AnimatedIcon name="warning" size={16} color="var(--sc-error)" />
            <span style={{ fontSize: 13, fontWeight: 500 }}>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmitWithVerify} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Phone Number</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <AnimatedIcon name="clipboard" size={16} color="var(--sc-text-tertiary)" />
              </span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+263 77 123 4567"
                style={styles.input}
                required
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={styles.label}>Password</label>
              <button type="button" style={styles.forgotText} onClick={openReset}>Forgot password?</button>
            </div>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <AnimatedIcon name="shield" size={16} color="var(--sc-text-tertiary)" />
              </span>
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                style={{ ...styles.input, paddingRight: 48 }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                style={styles.showPwdBtn}
              >
                <AnimatedIcon name={showPwd ? 'eye-off' : 'eye'} size={16} color="var(--sc-text-secondary)" />
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...styles.submitBtn,
              opacity: isLoading ? 0.75 : 1,
            }}
            className="sc-press"
          >
            {isLoading ? (
              <span style={styles.loadingFlex}>
                <span style={styles.spinner} /> Signing in...
              </span>
            ) : (
              <span style={styles.loadingFlex}>
                Sign In
                <AnimatedIcon name="pen" size={16} color="#FFF" />
              </span>
            )}
          </button>
        </form>

        {/* Social Login */}
        <div style={styles.socialDivider}>
          <div style={{ ...styles.dividerLine, background: 'var(--sc-border-light)' }} />
          <span style={{ ...styles.dividerText, fontWeight: 600, fontSize: 11, letterSpacing: '0.5px' }}>or continue with</span>
          <div style={{ ...styles.dividerLine, background: 'var(--sc-border-light)' }} />
        </div>
        <div style={styles.socialRow}>
          {[
            { label: 'Google', icon: 'google' as const, bg: '#FFF', text: '#3C4043', border: '#DADCE0' },
            { label: 'Facebook', icon: 'facebook' as const, bg: '#1877F2', text: '#FFF', border: '#1877F2' },
            { label: 'WhatsApp', icon: 'whatsapp' as const, bg: '#25D366', text: '#FFF', border: '#25D366' },
          ].map(s => (
            <button
              key={s.label}
              type="button"
              disabled
              style={{ ...styles.socialBtn, backgroundColor: '#E5E7EB', color: '#6B7280', borderColor: '#D1D5DB', cursor: 'not-allowed', opacity: 0.65 }}
            >
              <AnimatedIcon name={s.icon} size={16} color="#6B7280" />
              <span style={{ fontSize: 12, fontWeight: 700 }}>{s.label}</span>
            </button>
          ))}
        </div>

        <p style={styles.footerText}>
          Don't have an account?{' '}
          <Link to="/register" style={styles.signupLink}>Sign Up</Link>
        </p>
      </div>
      {resetOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalPanel}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Reset password</h3>
              <button type="button" style={styles.modalClose} onClick={() => setResetOpen(false)}>
                <AnimatedIcon name="cross" size={16} color="var(--sc-text-secondary)" />
              </button>
            </div>
            {resetMessage && <div style={styles.successBox}>{resetMessage}</div>}
            {resetError && <div style={styles.errorBox}>{resetError}</div>}
            {resetStep === 'email' ? (
              <form onSubmit={handleResetRequest} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Account email</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="name@example.com"
                    style={styles.input}
                    required
                  />
                </div>
                <button type="submit" disabled={resetLoading} style={styles.submitBtn}>
                  {resetLoading ? 'Sending...' : 'Send OTP'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetConfirm} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>OTP code</label>
                  <input
                    value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value)}
                    placeholder="6 digit code"
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>New password</label>
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="New password"
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Confirm password</label>
                  <input
                    type="password"
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    placeholder="Confirm password"
                    style={styles.input}
                    required
                  />
                </div>
                <button type="submit" disabled={resetLoading} style={styles.submitBtn}>
                  {resetLoading ? 'Resetting...' : 'Reset password'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'var(--sc-bg-primary)',
    padding: 16,
    fontFamily: 'var(--sc-font-family)',
  },
  card: {
    background: 'var(--sc-bg-secondary)',
    borderRadius: 'var(--sc-radius-xl)',
    padding: '40px 32px',
    width: '100%',
    maxWidth: 440,
    boxShadow: 'var(--sc-shadow-xl)',
    border: '1px solid var(--sc-border-light)',
    boxSizing: 'border-box',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 28,
  },
  logo: {
    width: 180,
    height: 50,
    objectFit: 'contain',
    filter: 'var(--sc-logo-filter)',
  },
  title: {
    fontSize: 24,
    fontWeight: 800,
    color: 'var(--sc-text-primary)',
    textAlign: 'center',
    margin: '0 0 6px',
    fontFamily: 'var(--sc-font-display)',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--sc-text-secondary)',
    textAlign: 'center',
    margin: '0 0 28px',
  },
  demoBox: {
    marginBottom: 24,
  },
  demoLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--sc-text-tertiary)',
    letterSpacing: '1px',
    margin: '0 0 12px',
    textAlign: 'center',
  },
  demoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  demoCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 6px',
    background: 'var(--sc-bg-input)',
    border: '1px solid var(--sc-border-light)',
    borderRadius: 'var(--sc-radius-md)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all var(--sc-transition-fast)',
  },
  demoIcon: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    marginBottom: 6,
  },
  demoRoleName: {
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--sc-text-primary)',
  },
  demoRoleDesc: {
    fontSize: 10,
    color: 'var(--sc-text-tertiary)',
    textAlign: 'center',
    marginTop: 2,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '24px 0',
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: 'var(--sc-border-light)',
  },
  dividerText: {
    fontSize: 12,
    color: 'var(--sc-text-tertiary)',
    whiteSpace: 'nowrap',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--sc-error-bg)',
    border: '1px solid var(--sc-error)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '12px 16px',
    color: 'var(--sc-error-text)',
    marginBottom: 20,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--sc-text-secondary)',
  },
  forgotText: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--sc-brand-primary)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: 14,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    height: 'var(--sc-touch-input)',
    padding: '0 16px 0 40px',
    fontSize: 14,
    fontFamily: 'inherit',
    color: 'var(--sc-text-primary)',
    background: 'var(--sc-bg-input)',
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all var(--sc-transition-fast)',
  },
  showPwdBtn: {
    position: 'absolute',
    right: 4,
    height: 40,
    width: 40,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitBtn: {
    marginTop: 8,
    height: 'var(--sc-touch-btn)',
    background: 'var(--sc-brand-gradient)',
    color: 'var(--sc-text-on-brand)',
    border: 'none',
    borderRadius: 'var(--sc-radius-md)',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: 'var(--sc-shadow-brand)',
    transition: 'all var(--sc-transition-fast)',
  },
  loadingFlex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  spinner: {
    width: 16,
    height: 16,
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTopColor: '#FFFFFF',
    borderRadius: '50%',
    animation: 'sc-spin 0.6s linear infinite',
  },
  footerText: {
    textAlign: 'center',
    fontSize: 13,
    color: 'var(--sc-text-secondary)',
    marginTop: 20,
    margin: 0,
  },
  signupLink: {
    color: 'var(--sc-brand-primary)',
    fontWeight: 700,
    textDecoration: 'none',
  },
  socialDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '20px 0 14px',
  },
  socialRow: {
    display: 'flex',
    gap: 8,
  },
  socialBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 42,
    border: '1px solid',
    borderRadius: 'var(--sc-radius-md)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.15s',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.48)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
  },
  modalPanel: {
    width: '100%',
    maxWidth: 420,
    background: 'var(--sc-bg-secondary)',
    border: '1px solid var(--sc-border-light)',
    borderRadius: 'var(--sc-radius-lg)',
    boxShadow: 'var(--sc-shadow-xl)',
    padding: 24,
    boxSizing: 'border-box',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  modalTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
    color: 'var(--sc-text-primary)',
  },
  modalClose: {
    width: 34,
    height: 34,
    border: '1px solid var(--sc-border-light)',
    borderRadius: 8,
    background: 'var(--sc-bg-input)',
    cursor: 'pointer',
  },
  successBox: {
    background: 'var(--sc-success-bg)',
    border: '1px solid var(--sc-success)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '10px 12px',
    color: 'var(--sc-success-text)',
    fontSize: 13,
    marginBottom: 14,
  },
};
